import { PLATFORM_TIMEZONE } from "./timezone";

/**
 * The timezone every date on screen is rendered in: always Asia/Baghdad (PLATFORM_TIMEZONE).
 *
 * Fixed by product decision, so a centre user travelling abroad still sees the clock the centre
 * works on. Kept as a function so the call sites need no change.
 */
export const getDisplayTimezone = () => PLATFORM_TIMEZONE;

/** Shown when a value is absent or unparseable. */
const EMPTY = "—";

const toDate = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * `en-US` because that is the locale the existing call sites already used.
 *
 * Deliberate restraint: this is about WHICH CLOCK a time is shown on, not about restyling dates.
 * Changing the locale as well would quietly alter every screen — a change nobody asked for, mixed
 * into one they did.
 */
const LOCALE = "en-US";

/**
 * The one place `timeZone` is applied.
 *
 * Every formatter below goes through here, so there is a single answer to "which clock is this?".
 * `Intl` handles daylight saving and historical offsets itself, which is why none of this needs date
 * maths of its own — the entire fix is passing the zone it was never given before.
 */
const formatIn = (value, locale, options) => {
  const date = toDate(value);
  if (!date) return EMPTY;
  try {
    return new Intl.DateTimeFormat(locale || LOCALE, {
      ...options,
      timeZone: getDisplayTimezone(),
    }).format(date);
  } catch {
    // An environment without timezone data must not blank the screen. Fall back to the browser's zone.
    try {
      return new Intl.DateTimeFormat(locale || LOCALE, options).format(date);
    } catch {
      return EMPTY;
    }
  }
};

const format = (value, options) => formatIn(value, LOCALE, options);

/**
 * Same as `formatCustom` but keeping an explicitly chosen locale.
 *
 * Needed because a handful of screens deliberately render `en-GB` (29/09/2026 rather than
 * 9/29/2026). Forcing those to the default locale would change the day/month order — a silent
 * restyle on top of the timezone fix, and the kind that is easy to misread rather than merely notice.
 */
export const formatCustomInLocale = (value, locale, options) =>
  formatIn(value, locale, options);

/** `Jul 15, 2026` */
export const formatDate = (value) =>
  format(value, { year: "numeric", month: "short", day: "numeric" });

/** `July 15, 2026` — long month, for headings and detail views. */
export const formatDateLong = (value) =>
  format(value, { year: "numeric", month: "long", day: "numeric" });

/** `Jul 15, 2026, 05:30 PM` */
export const formatDateTime = (value) =>
  format(value, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

/** `Jul 15, 2026, 05:30:09 PM` — where the exact second matters. */
export const formatDateTimeWithSeconds = (value) =>
  format(value, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

/** `05:30 PM` */
export const formatTime = (value) =>
  format(value, { hour: "2-digit", minute: "2-digit" });

/** `Jul 15` — compact labels. */
export const formatDayMonth = (value) =>
  format(value, { month: "short", day: "numeric" });

/*
 * The three below exist to replace a BARE `toLocale*String()` without changing how it looks.
 *
 * A bare call renders numerically ("9/29/2026", "4:48:26 PM"). Routing those through the nicer named
 * formatters above would silently restyle dates all over the app, so these reproduce the default
 * shapes exactly and change only the clock they are rendered on.
 */

/** Matches a bare `toLocaleDateString()` — e.g. `9/29/2026`. */
export const formatDateNumeric = (value) =>
  format(value, { year: "numeric", month: "numeric", day: "numeric" });

/** Matches a bare `toLocaleTimeString()` — e.g. `4:48:26 PM`. */
export const formatTimeNumeric = (value) =>
  format(value, { hour: "numeric", minute: "2-digit", second: "2-digit" });

/** Matches a bare `toLocaleString()` — e.g. `9/29/2026, 4:48:26 PM`. */
export const formatDateTimeNumeric = (value) =>
  format(value, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });

/** `Wednesday` */
export const formatWeekday = (value) => format(value, { weekday: "long" });

/**
 * Escape hatch for a one-off shape, still in the right zone.
 *
 * Prefer the named formatters: consistent dates across the app is most of the value here, and an
 * options object at the call site is how that drifts.
 */
export const formatCustom = (value, options) => format(value, options);

/**
 * `2026-07-15` in the DISPLAY zone — the calendar day the user is looking at.
 *
 * For the specific case of turning an instant into a day key for display or comparison. NOT for
 * building API payloads from a user-picked date: a date chosen in a picker is already a calendar day
 * with no instant behind it, and pushing it through a timezone would shift which day the server is
 * asked about. Those call sites deliberately still use date-fns `format(d, "yyyy-MM-dd")`.
 */
export const formatIsoDateInDisplayZone = (value) => {
  const date = toDate(value);
  if (!date) return "";
  try {
    // en-CA renders ISO-ordered yyyy-mm-dd, which avoids assembling it from parts by hand.
    return new Intl.DateTimeFormat("en-CA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      timeZone: getDisplayTimezone(),
    }).format(date);
  } catch {
    return "";
  }
};

/** The zone itself, for a label like "times shown in Asia/Baghdad". */
export const displayTimezoneLabel = () => getDisplayTimezone();

/**
 * "Now" on the centre's clock (Baghdad): today's date (`yyyy-MM-dd`) and minutes since midnight.
 *
 * For "is this slot already past?" checks against the centre's operating hours, which are Baghdad
 * wall-clock times. The device clock would be wrong for a user travelling abroad.
 */
export const nowInDisplayZone = () => {
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: getDisplayTimezone(),
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).formatToParts(new Date());
    const get = (type) => parts.find((p) => p.type === type)?.value || "";
    return {
      date: `${get("year")}-${get("month")}-${get("day")}`,
      minutes: Number(get("hour")) * 60 + Number(get("minute")),
    };
  } catch {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return {
      date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
      minutes: d.getHours() * 60 + d.getMinutes(),
    };
  }
};
